import {Box, Button, LinearProgress, Stack, styled, Typography} from '@mui/material';
import {type OptimizationRun, OptimizationRunStateEnum} from 'client';
import {EMPTY_ID} from 'hooks/swr';
import {SWRKeyEnum} from 'interfaces';
import {type ReactNode, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import backendCommunicator from 'services/backendCommunicator';
import {mutate} from 'swr';
import Alert from 'helpers/Alert';

export const AppTopBarOptimizationProgress = ({optimizationRun}: {optimizationRun: OptimizationRun}) => {
  const {t} = useTranslation('common');
  const {progress, elapsedTime, state} = optimizationRun;
  const [canceled, setCanceled] = useState(false);

  const cancelRun = () => {
    backendCommunicator
      .cancelOptimizationsRun(optimizationRun.id)
      .then(() => {
        void mutate([EMPTY_ID, SWRKeyEnum.GET_OPTIMIZATION_RUNS]);
        setCanceled(true);
      })
      .catch((error) => {
        Alert.error(error);
      });
  };

  const getDisplayedStatus = (state?: OptimizationRunStateEnum): ReactNode => {
    switch (state) {
      case OptimizationRunStateEnum.Pending:
        return `${t('starting')}...`;
      case OptimizationRunStateEnum.Started:
        return <OptimizationTimer startSeconds={elapsedTime ?? 0} />;
      case OptimizationRunStateEnum.Failure:
        return t('failure');
      case OptimizationRunStateEnum.Success:
        return t('success');
    }
    return '';
  };

  if (canceled) {
    return null;
  }

  return (
    <Stack direction="row" spacing={2} pl={0}>
      <Stack direction="column" alignItems="stretch" spacing={0.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2">{t('optimizing')}</Typography>
          <Typography variant="body2">{getDisplayedStatus(state)}</Typography>
        </Stack>
        <LinearProgress sx={{width: 200}} variant="determinate" value={Math.trunc((progress ?? 0) * 100)} />
      </Stack>
      <Button size="small" variant="contained" onClick={cancelRun}>
        {t('cancel')}
      </Button>
    </Stack>
  );
};

const AppTopBarSyncronizationProgress = () => {
  const {t} = useTranslation('common');

  return (
    <ProgressBox display="flex" alignItems="center">
      <Typography variant="body1" color="textPrimary">
        {t('synchronization')}
      </Typography>
      <LinearProgress style={{flexGrow: 1, marginTop: 4}} />
    </ProgressBox>
  );
};

const OptimizationTimer = ({startSeconds}: {startSeconds: number}) => {
  const [secondsPassed, setSecondsPassed] = useState<number>(startSeconds);

  useEffect(() => {
    let secondsTimer = secondsPassed;
    const timerId = setInterval(() => {
      setSecondsPassed(secondsTimer++);
    }, 1000);
    return () => {
      clearInterval(timerId);
    };
  }, []);

  return <>{toTimeString(secondsPassed)}</>;
};

const ProgressBox = styled(Box)(({theme}) => ({
  width: theme.spacing(40),
  '&.MuiBox-root > *': {
    margin: `0px ${theme.spacing(0.75)}`,
  },
}));

const toTimeString = (seconds: number): string => {
  seconds = Math.floor(seconds);
  const minutes = Math.floor(seconds / 60);
  seconds = seconds - minutes * 60;
  const minutesString: string = minutes < 10 ? '0' + minutes : minutes.toString();
  const secondsString: string = seconds < 10 ? '0' + seconds : seconds.toString();
  return minutesString + ':' + secondsString;
};
